import { Container as PixiReactContainer } from "@pixi/react"
import { t } from "i18next"
import { GRAPH_CONTAINER_NAME } from "@/domains/visualization/constants/graph.ts"
import { useElkLayout } from "@/domains/visualization/hooks/useElkLayout/useElkLayout.ts"
import { usePanAndZoom } from "@/domains/visualization/hooks/usePanAndZoom.ts"
import type { Edge } from "@/domains/visualization/types/Edge.ts"
import type { Vertex } from "@/domains/visualization/types/Vertex.ts"
import { CanvasLoadingSpinner } from "../CanvasLoadingSpinner/CanvasLoadingSpinner.tsx"
import { GraphEdge } from "../GraphEdge/GraphEdge.tsx"
import { GraphNode } from "../GraphNode/GraphNode.tsx"

interface GraphPreviewCanvasProps {
  nodes: Vertex[]
  edges: Edge[]
  wrapperRef: React.RefObject<HTMLDivElement>
  canvasWidth: number
  canvasHeight: number
  disablePanAndZoom: boolean
}

export function GraphPreviewCanvas({
  nodes,
  edges,
  wrapperRef,
  canvasWidth,
  canvasHeight,
  disablePanAndZoom
}: GraphPreviewCanvasProps) {
  const { positionedNodes, positionedEdges, initialZoom, initialLayoutReady } = useElkLayout(
    nodes,
    edges,
    canvasWidth,
    canvasHeight
  )

  usePanAndZoom({
    containerName: `${GRAPH_CONTAINER_NAME}-preview`,
    wrapperRef,
    initialZoom,
    disabled: disablePanAndZoom
  })

  const shouldRender = canvasWidth > 0 && canvasHeight > 0 && positionedNodes.length > 0

  return (
    <PixiReactContainer name={`${GRAPH_CONTAINER_NAME}-preview`}>
      {!initialLayoutReady ? (
        <CanvasLoadingSpinner x={canvasWidth / 2} y={canvasHeight / 2} text={t("visualization.creatingGraph")} />
      ) : (
        shouldRender && (
          <>
            {positionedEdges.map((edge) => (
              <GraphEdge key={edge.id} edge={edge} />
            ))}
            {positionedNodes.map((node) => (
              <GraphNode key={node.id} node={node} />
            ))}
          </>
        )
      )}
    </PixiReactContainer>
  )
}
